import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import {
  CreditCard,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Upload,
  X,
  Image as ImageIcon
} from 'lucide-react'
import { SkeletonTable } from '../components/Skeleton'
import toast from 'react-hot-toast'

const Subscription = () => {
  const navigate = useNavigate()
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const [formData, setFormData] = useState({
    collectionName: '',
    amount: '',
    isFree: false,
    discountPlan: '',
    index: '',
    image: null
  })
  const [imagePreview, setImagePreview] = useState('')
  const {
    uploadImage,
    getSubscriptions,
    addSubscription,
    updateSubscription,
    deleteSubscription,
    getSubscriptionById,
    getSubscriptionMeta,
    bulkSyncSubscriptionIndices,
    copyHomeContentToSubscription, // Kept for legacy if needed, but createSubscriptionCard is preferred
    createSubscriptionCard
  } = useFirebase()


  useEffect(() => {
    loadSubscriptions()
  }, [])

  useEffect(() => {
    if (showAddModal) {
      fetchNextIndex()
    }
  }, [showAddModal])

  const fetchNextIndex = async () => {
    try {
      // 1. Check currently loaded subscriptions (fastest)
      const currentMax = subscriptions.length > 0
        ? Math.max(...subscriptions.map(s => Number(s.index) || 0))
        : 0

      // 2. Double check with database meta tracker
      const meta = await getSubscriptionMeta()
      const metaMax = meta?.lastIndex || 0

      // The new index is strictly 1 higher than the absolute maximum identified
      const suggestedIndex = Math.max(currentMax, metaMax) + 1

      setFormData(prev => ({ ...prev, index: suggestedIndex.toString() }))
    } catch (error) {
      console.warn('Could not fetch next index:', error)
      const fallbackIndex = Math.max(subscriptions.length, 0) + 1
      setFormData(prev => ({ ...prev, index: fallbackIndex.toString() }))
    }
  }

  const loadSubscriptions = async () => {
    try {
      setLoading(true)
      const data = await getSubscriptions()

      // Sort subscriptions: Try to use index, then createdAt
      const sortedData = (data || []).sort((a, b) => {
        // 1. Sort by index (highest priority)
        // If index is missing, treat it as a very high number to push it to the end
        const idxA = (a.index !== undefined && a.index !== null && a.index !== '') ? Number(a.index) : 10000
        const idxB = (b.index !== undefined && b.index !== null && b.index !== '') ? Number(b.index) : 10000

        if (idxA !== idxB) {
          return idxA - idxB
        }

        // 2. Sort by createdAt (fallback for items with same index OR legacy items)
        if (a.createdAt && b.createdAt) {
          const timeA = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)
          const timeB = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)
          // For legacy items, older ones should come first at the bottom
          return timeA - timeB
        }

        // 3. Absolute Fallback to ID
        return (a.id || '').localeCompare(b.id || '')
      })

      setSubscriptions(sortedData)
    } catch (error) {
      console.error('Error loading subscriptions:', error)
      toast.error('Failed to load subscriptions')
    } finally {
      setLoading(false)
    }
  }

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        setFormData(prev => ({ ...prev, image: file }))
        const reader = new FileReader()
        reader.onloadend = () => {
          setImagePreview(reader.result)
        }
        reader.readAsDataURL(file)
        toast.success('Image selected successfully!')
      } else {
        toast.error('Please upload an image file')
      }
    }
  }

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }))
    setImagePreview('')
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name === 'isFree') {
      setFormData(prev => ({
        ...prev,
        isFree: checked,
        amount: checked ? '' : prev.amount
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleCancel = () => {
    setShowAddModal(false)
    setShowEditModal(false)
    setShowViewModal(false)
    setSelectedSubscription(null)
    setFormData({
      collectionName: '',
      amount: '',
      isFree: false,
      discountPlan: '',
      index: '',
      image: null
    })
    setImagePreview('')
  }

  const handleSave = async () => {
    if (!formData.collectionName.trim() || (!formData.isFree && !formData.amount)) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      setSaving(true)

      // Prevent duplicate names when adding new subscription
      if (!showEditModal) {
        const nameExists = subscriptions.some(s =>
          (s.CollectionName || s.collectionName || s.id || '').toLowerCase() === formData.collectionName.trim().toLowerCase()
        )
        if (nameExists) {
          toast.error('A subscription with this name already exists!')
          setSaving(false)
          return
        }
      }

      let imageUrl = imagePreview // Keep existing image URL if no new image

      // Upload new image if selected
      if (formData.image) {
        imageUrl = await uploadImage(formData.image, 'subscriptions')
      }

      const subscriptionData = {
        collectionName: formData.collectionName.trim(),
        amount: formData.isFree ? 'Free' : formData.amount,
        isFree: formData.isFree,
        discountPlan: formData.discountPlan || '',
        imageUrl: imageUrl || '',
        index: formData.index || undefined
      }

      if (showEditModal && selectedSubscription) {
        await updateSubscription(selectedSubscription.id, subscriptionData)
        toast.success('Subscription updated successfully!')
      } else {
        await addSubscription(subscriptionData)
        toast.success('Subscription added successfully!')
      }

      handleCancel()
      loadSubscriptions()
    } catch (error) {
      console.error('Error saving subscription:', error)
      toast.error('Failed to save subscription')
    } finally {
      setSaving(false)
    }
  }

  const handleView = (subscription) => {
    setSelectedSubscription(subscription)
    setShowViewModal(true)
  }

  const handleEdit = (subscription) => {
    setSelectedSubscription(subscription)
    setFormData({
      collectionName: subscription.CollectionName || subscription.collectionName || subscription.id,
      amount: subscription.SubscriptionAmount || subscription.amount || '',
      isFree: (subscription.SubscriptionAmount === 'Free' || subscription.amount === 'Free') || subscription.isFree || false,
      discountPlan: subscription.discountPlan || '',
      index: (subscription.index || '').toString(),
      image: null
    })
    setImagePreview(subscription.collectionImageUrl || subscription.imageUrl || '')
    setShowEditModal(true)
  }

  const handleDelete = async (id, collectionName) => {
    const name = collectionName || id
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteSubscription(id)
        toast.success('Subscription deleted successfully!')
        loadSubscriptions()
      } catch (error) {
        console.error('Error deleting subscription:', error)
        toast.error('Failed to delete subscription')
      }
    }
  }

  const handleAddCards = (subscription) => {
    navigate(`/subscription/${subscription.id}/add-cards`, {
      state: { subscription }
    })
  }

  const handleBulkSync = async () => {
    if (window.confirm('This will automatically assign sequential index numbers (1, 2, 3...) to ALL subscriptions based on their creation date. This fixes the "dashed" items and restores order. Proceed?')) {
      try {
        setLoading(true)
        await bulkSyncSubscriptionIndices()
        toast.success('Successfully re-indexed all subscriptions!')
        await loadSubscriptions()
      } catch (error) {
        console.error('Error in bulk sync:', error)
        toast.error('Failed to sync indices')
      } finally {
        setLoading(false)
      }
    }
  }

  const filteredSubscriptions = subscriptions.filter(sub => {
    const name = sub.CollectionName || sub.collectionName || sub.id || ''
    return name.toLowerCase().includes(searchTerm.toLowerCase())
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <CreditCard className="h-8 w-8 mr-3" />
              Subscriptions
            </h1>
            <p className="text-indigo-100">Manage subscription plans and pricing</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleBulkSync}
              className="bg-indigo-500 text-white border border-indigo-400 px-4 py-3 rounded-lg font-medium hover:bg-indigo-400 transition-colors flex items-center gap-2 shadow-sm"
              title="Fix sequential numbering for all items"
            >
              Sync Sequential Order
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition-colors flex items-center gap-2 shadow-lg"
            >
              <Plus className="h-5 w-5" />
              Add Subscription
            </button>
          </div>
        </div>
      </div>
      {/* Warning for unindexed items */}
      {subscriptions.some(s => s.index === undefined || s.index === null || s.index === '') && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-xl shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.439-1.196 2.142-1.196 2.581 0l6.091 16.591a1.25 1.25 0 01-1.29 1.723H4.362a1.25 1.25 0 01-1.29-1.723l6.091-16.591zm1.743 1.901l-6.091 16.591h12.182L10 5.0zM9 14a1 1 0 112 0 1 1 0 01-2 0zm1-7a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700 font-medium">
                Some items are missing sequential indices. This may cause unpredictable sorting.
                <button
                  onClick={handleBulkSync}
                  className="ml-2 underline hover:text-amber-800 font-bold"
                >
                  Click here to auto-fix and re-order all items.
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Search */}
      <div className="card p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search subscriptions by collection name..."
            className="input pl-10 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Subscription Plans ({filteredSubscriptions.length})
          </h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <SkeletonTable rows={5} columns={7} />
          ) : filteredSubscriptions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Index</th>
                    <th>Image</th>
                    <th>Collection Name</th>
                    <th>Subscription Amount</th>
                    <th>Cards Count</th>
                    <th>Discount Plan</th>
                    <th>Add Cards</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptions.map((subscription, index) => (
                    <tr key={subscription.id} className="hover:bg-gray-50">
                      <td>
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-bold text-sm border border-gray-200">
                          {subscription.index !== undefined ? subscription.index : '—'}
                        </span>
                      </td>
                      <td>
                        {(subscription.collectionImageUrl || subscription.imageUrl) ? (
                          <img
                            src={subscription.collectionImageUrl || subscription.imageUrl}
                            alt={subscription.CollectionName || subscription.collectionName}
                            className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center border border-gray-300">
                            <ImageIcon className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="font-semibold text-gray-900">
                          {subscription.CollectionName || subscription.collectionName || subscription.id}
                        </div>
                      </td>
                      <td>
                        <span className="text-lg font-bold text-indigo-600">
                          ₹{subscription.SubscriptionAmount || subscription.amount || 0}
                        </span>
                      </td>
                      <td>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {subscription.cardsCount || 0} cards
                        </span>
                      </td>
                      <td>
                        {subscription.discountPlan ? (
                          <span className="text-sm font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                            {subscription.discountPlan}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm italic">No discount</span>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => handleAddCards(subscription)}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
                          title="Add Cards"
                        >
                          <Plus className="h-5 w-5" />
                        </button>
                      </td>
                      <td>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleView(subscription)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="View"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEdit(subscription)}
                            className="text-green-600 hover:text-green-900 transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(subscription.id, subscription.CollectionName || subscription.collectionName)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No subscriptions found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first subscription plan'
                }
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="btn-primary"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Subscription
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Subscription Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancel} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header - Sticky */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">
                {showEditModal ? 'Edit Subscription Plan' : 'Add Subscription Plan'}
              </h2>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Index No. *
                  </label>
                  <input
                    type="number"
                    name="index"
                    value={formData.index}
                    onChange={handleInputChange}
                    className="input w-full"
                    placeholder="Enter index number..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">This determines the display order of the package.</p>
                </div>

                {/* Collection Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Collection Name *
                  </label>
                  <input
                    type="text"
                    name="collectionName"
                    value={formData.collectionName}
                    onChange={handleInputChange}
                    className="input w-full"
                    placeholder="Enter collection name..."
                    required
                  />
                </div>

                {/* Subscription Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subscription Amount (₹) *
                  </label>
                  <div className="space-y-3">
                    {/* Free Checkbox */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isFree"
                        name="isFree"
                        checked={formData.isFree}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="isFree" className="ml-2 text-sm text-gray-700">
                        Free Subscription
                      </label>
                    </div>

                    {/* Amount Input */}
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      disabled={formData.isFree}
                      className={`input w-full ${formData.isFree ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder="Enter amount..."
                      min="0"
                      step="0.01"
                      required={!formData.isFree}
                    />
                  </div>
                </div>

                {/* Discount Plan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Plan
                  </label>
                  <input
                    type="text"
                    name="discountPlan"
                    value={formData.discountPlan}
                    onChange={handleInputChange}
                    className="input w-full"
                    placeholder="Enter discount plan (e.g., 10% off, Festive offer)..."
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Image
                  </label>

                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200"
                      />
                      <button
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 hover:bg-gray-50 transition-colors">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Click to upload image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Subscription Modal */}
      {showViewModal && selectedSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancel} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header - Sticky */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-xl font-semibold text-white">Subscription Details</h2>
              <button
                onClick={handleCancel}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
              {/* Image */}
              {(selectedSubscription.collectionImageUrl || selectedSubscription.imageUrl) && (
                <div className="flex justify-center">
                  <img
                    src={selectedSubscription.collectionImageUrl || selectedSubscription.imageUrl}
                    alt={selectedSubscription.CollectionName || selectedSubscription.collectionName}
                    className="w-48 h-48 rounded-lg object-cover border-2 border-gray-200 shadow-lg"
                  />
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Collection Name
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedSubscription.CollectionName || selectedSubscription.collectionName}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Subscription Amount
                  </label>
                  <p className="text-lg font-bold text-indigo-600">
                    ₹{selectedSubscription.SubscriptionAmount || selectedSubscription.amount}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Cards Count
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedSubscription.cardsCount || 0} cards
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Discount Plan
                  </label>
                  <p className="text-lg font-semibold text-green-600">
                    {selectedSubscription.discountPlan || 'None'}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer - Sticky */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 sticky bottom-0 z-10">
              <button
                onClick={handleCancel}
                className="btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  handleEdit(selectedSubscription)
                }}
                className="btn-primary"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Subscription
